// The analytics event catalogue: every name, the sinks it goes to, and the
// parameters its call site passes. Parameters marked "filled in" are
// createVisits's own and a call site may leave them out.
//
// Play-only game events (fights, saves) are not listed here: this table ships in
// every bundle, and explore's must not carry their names.

export type Sink = 'ga' | 'ow';
export type ParamValue = string | number | boolean;

/** The Atlas's bands, as `bandAt` in src/atlas/camera.ts names them. */
export type BandName = 'SYSTEM' | 'ORBIT' | 'REGIONAL' | 'LOCAL' | 'CLAIM' | 'PLOT' | 'SURFACE';
export type FocusSource = 'orrery' | 'list' | 'url' | 'land';
export type ExceptionKind = 'context_lost' | 'tile' | 'manifest' | 'descent' | 'error' | 'rejection';

export interface EventParams {
  visit: {
    referrer: string; entry_view: string;
    utm_source?: string; utm_medium?: string; utm_campaign?: string;
  };
  app_ready: {
    entry_view: string; load_ms: number; webgl?: 1 | 2; input: 'touch' | 'mouse'; screen: 's' | 'm' | 'l';
  };
  page_view: { page_title: string; page_location: string };
  atlas_focus: { body_id: string; body_class: string; source: FocusSource };
  atlas_tier: { body_id: string; band: BandName; tier: number };
  /** `from` and `s_since_focus` filled in. */
  surface_reached: { body_id: string; from?: 'atlas' | 'land'; s_since_focus?: number };
  /** `site_slug`, not `site`: every event's `site` already names play or explore. */
  walkaround_start: { body_id: string; site_slug: string; build_ms: number };
  /** `seconds` filled in; dropped when no walkaround was started. */
  walkaround_end: { body_id: string; seconds?: number };
  rover_deploy: { body_id: string };
  rover_drive: { body_id: string; odometer_m: number; seconds: number };
  trip_planned: {
    origin_id: string; target_id: string; dv_kms: number; tof_days: number; wait_days: number;
    source: 'list' | 'name';
  };
  trip_start: { origin_id: string; target_id: string; dv_kms: number; tof_days: number };
  trip_end: {
    target_id: string; outcome: 'arrived' | 'handback' | 'refused' | 'cancelled';
    sim_days: number; real_s: number; skips: number;
  };
  /** Held and sent 3 s after the last pick, with the first pick's `from_level`. */
  warp_set: { level: string; from_level: string };
  warp_skip: { target_id: string; skipped_days: number };
  land_start: { body_id: string };
  /** `seconds` filled in. */
  launch: { body_id: string; seconds?: number };
  names_toggle: { on: boolean; source: 'button' | 'key' };
  name_click: { body_id: string; result: 'picked' | 'cleared' | 'no_plan' };
  ui_open: { ui: string; action: 'open' | 'close' };
  /** `detail` is scrubbed and cut to 100 characters before it is kept. */
  exception: { kind: ExceptionKind; detail: string; body_id?: string; fatal: boolean };
  perf_summary: {
    view: string; frames: number; p50_ms: number; p95_ms: number; p99_ms: number; long_frames: number;
    render_scale_min: number; draws: number; textures: number; geometries: number; heap_mb?: number;
    tile_mb: number; tiles: number; tile_fails: number;
  };
  visit_end: {
    s_solar: number; s_cruise: number; s_land: number; s_walk: number; deepest_band: BandName | 'none';
    bodies: number; trips: number; errors: number; p95_ms: number;
  };
}

export type EventName = keyof EventParams;

const GA = ['ga'] as const;
const OW = ['ow'] as const;
const BOTH = ['ga', 'ow'] as const;

/** Where each event goes. The relay's own allowlist of OW names is checked against this. */
export const ROUTES = {
  visit: OW,
  app_ready: BOTH,
  page_view: GA,
  atlas_focus: GA,
  atlas_tier: GA,
  surface_reached: GA,
  walkaround_start: BOTH,
  walkaround_end: GA,
  rover_deploy: GA,
  rover_drive: BOTH,
  trip_planned: GA,
  trip_start: BOTH,
  trip_end: BOTH,
  warp_set: GA,
  warp_skip: GA,
  land_start: BOTH,
  launch: GA,
  names_toggle: GA,
  name_click: GA,
  ui_open: GA,
  exception: GA,
  perf_summary: GA,
  visit_end: OW,
} as const satisfies { readonly [N in EventName]: readonly Sink[] };

export const EVENT_NAMES = Object.keys(ROUTES) as EventName[];

/** The events routed to Overwatch, which the relay must know by name. */
export type OwRoutedName = { [N in EventName]: 'ow' extends (typeof ROUTES)[N][number] ? N : never }[EventName];
